import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import apiClient from "../api/apiClient";
import StripeCheckoutModal from "../modals/StripeCheckoutModal";
import { CloseIcon } from "./icons";
import { MAX_MOCKUPS, listingAccessAllowed, listingPreviewImage, listingPrice, listingTitle } from "./listings";

/**
 * Mockup details from Discover, My mockups or the preview's info button.
 * Owned mockups can be added to the editor; others are bought with the
 * site's Stripe checkout and then added.
 */
export default function ProductDialog({ listing, template, inEditor, mockupCount, detailsOnly = false, onClose, onAdd, onPurchased }) {
  const { role, userid } = useSelector((state) => state.auth);
  const isAdmin = role === "A";
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkout, setCheckout] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const id = listing?.id;

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/listings/${id}/`).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.ok) setDetail(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!listing) return null;
  const data = detail || listing;
  const owned = listingAccessAllowed(data, { isAdmin, userid });
  const price = listingPrice(data);
  const full = !inEditor && mockupCount >= MAX_MOCKUPS;
  const title = listingTitle(data);
  const facts = [
    ["Size", template ? `${template.docWidth} × ${template.docHeight} px` : null],
    ["Print area", template ? `${template.designWidth} × ${template.designHeight} px` : null],
    ["Type", data.ai_models],
    ["Product name", data.product_name],
    ["Print provider", data.print_provider],
  ].filter(([, value]) => value);

  const handlePaid = async () => {
    setCheckout(false);
    setConfirming(true);
    // The sale is recorded by /stripeSuccess/; reload to pick up ownership.
    const result = await apiClient.get(`/listings/${id}/`);
    setConfirming(false);
    const fresh = result.ok ? result.data : { ...data, is_bought: true };
    setDetail(fresh);
    onPurchased?.(fresh);
  };

  let action = null;
  if (!detailsOnly) {
    // A mockup can sit in the editor before it is bought (opened from its page).
    if (!owned) action = { label: `Buy · ${price.label}`, onClick: () => setCheckout(true) };
    else if (inEditor) action = { label: "Show in editor", onClick: () => onAdd(data) };
    else if (full) action = { label: `Editor full · ${MAX_MOCKUPS}/${MAX_MOCKUPS}`, disabled: true };
    else action = { label: "Add to editor", onClick: () => onAdd(data) };
  }

  return (
    <>
      <Modal
        isOpen={!checkout}
        onRequestClose={confirming ? undefined : onClose}
        className="sw-dialog sw-product-dialog"
        overlayClassName="sw-dialog-overlay"
        contentLabel={title}
      >
        <button type="button" className="sw-icon-button sw-dialog-close" onClick={onClose} aria-label="Close mockup details" disabled={confirming}>
          <CloseIcon />
        </button>
        <div className="sw-product-image">
          <img src={listingPreviewImage(data)} alt={title} />
        </div>
        <div className="sw-product-info">
          <span className="sw-eyebrow">{owned ? "My mockups" : "Discover"}</span>
          <h2>{title}</h2>
          {data.short_description && <p className="sw-product-lead">{data.short_description}</p>}
          <dl>
            {facts.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {data.long_description && (
            <>
              <h3>Description</h3>
              <p className="sw-product-description">{data.long_description}</p>
            </>
          )}
          <div className="sw-creator-line">
            <span>Mockup by</span>
            {data.user_id ? <Link to={`/browse-mockups?name=${data.user_id}`}>{data.user_name || "Creator"}</Link> : <strong>{data.user_name || "—"}</strong>}
          </div>
          {!detailsOnly &&
            (owned ? (
              <div className="sw-purchase-note is-owned">✓ {inEditor ? "Already in your editor" : "In My mockups"}</div>
            ) : (
              <div className="sw-purchase-note">
                <strong>
                  {price.label}
                  {price.original && <s>{price.original}</s>}
                </strong>
                <p>After purchase, this mockup is added to your editor and to My mockups.</p>
              </div>
            ))}
          {full && !detailsOnly && <p className="sw-limit-note">Remove a mockup from the editor to add another.</p>}
          <div className="sw-product-actions">
            <button type="button" className="sw-button" onClick={onClose} disabled={confirming}>
              {detailsOnly ? "Close" : "Cancel"}
            </button>
            {action && (
              <button type="button" className="sw-button sw-primary" onClick={action.onClick} disabled={action.disabled || loading || confirming}>
                {loading || confirming ? <CircularProgress size={16} sx={{ color: "currentColor" }} /> : action.label}
              </button>
            )}
          </div>
          <Link className="sw-listing-link" to={`/mockup?id=${id}`}>
            View listing page
          </Link>
        </div>
      </Modal>
      {checkout && (
        <StripeCheckoutModal
          open
          onClose={() => setCheckout(false)}
          items={[{ product_id: id, modelType: "listing" }]}
          subtotal={price.amount}
          onSuccess={handlePaid}
        />
      )}
    </>
  );
}
